import { Body4, Button } from '@cointracker/styleguide/src/LoggedIn';
import { ArrowClockwise } from '@phosphor-icons/react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTaxReadinessStore } from '../../utils';

interface OptimizeTaxSavingsContentProps {
  onClickPrimary: () => void;
  onClickSecondary: () => void;
  transactionCount: number;
  errorsCount: number;
}

export const OptimizeTaxSavingsContent = ({
  onClickPrimary,
  onClickSecondary,
  transactionCount,
  errorsCount,
}: OptimizeTaxSavingsContentProps) => {
  const { t } = useTranslation();
  const [isRecalculating, setIsRecalculating] = useState(false);
  const { setOptimizeTaxSavingsCheckedByUser } = useTaxReadinessStore();

  const handleRecalculate = () => {
    setIsRecalculating(true);
    onClickSecondary();
    setTimeout(() => setIsRecalculating(false), 2000);
  };

  const errorPercentage = Math.round(
    errorsCount > 0 ? Math.max((errorsCount / transactionCount) * 100, 3) : 0,
  );
  const accuratePercentage = 100 - errorPercentage;

  const description =
    errorsCount > 10
      ? t(
          'navigation.tax_readiness.sheet.optimise_tax_savings_content.description_with_many',
        )
      : t(
          'navigation.tax_readiness.sheet.optimise_tax_savings_content.description',
          { count: errorsCount, value: errorsCount },
        );

  return (
    <div className="flex flex-col gap-16 pt-16">
      <Body4>{description}</Body4>

      <div className="rounded-8 bg-background-alternate-lighter p-16">
        <div className="relative flex h-16 w-full rounded-full">
          <div
            className="h-full rounded-l-8 bg-green-15"
            style={{ width: `${accuratePercentage}%` }}
            aria-label="Transactions with accurate cost basis"
          />
          <div
            className="h-full rounded-r-8 bg-yellow-20"
            style={{ width: `${errorPercentage}%` }}
            aria-label="Transactions with cost basis issues"
          />
          <div
            className="border-blue-400 absolute -bottom-10 -top-10 border-l-2 border-dashed"
            style={{ left: `${accuratePercentage}%` }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-8">
        <Trans i18nKey="navigation.tax_readiness.sheet.optimise_tax_savings_content.action">
          <Body4>
            Review these transactions, and then recalculate the cost basis to
            run an accuracy check. You can also{' '}
            <button
              className="text-text-link underline"
              onClick={() => setOptimizeTaxSavingsCheckedByUser(true)}
            >
              skip this step
            </button>{' '}
            and generate your forms.{' '}
            <a
              href="https://support.cointracker.io/hc/en-us/articles/14327061214865-Understand-review-suggested-flags"
              target="_blank"
              className="text-text-link"
              rel="noopener noreferrer"
            >
              Learn more
            </a>{' '}
            about reviewing transactions.
          </Body4>
        </Trans>
      </div>

      <div className="flex w-full gap-16">
        <Button
          size="small"
          variant="highEmphasis"
          onClick={onClickPrimary}
          className="flex-1 px-16 py-8"
        >
          {t('button.review_transactions')}
        </Button>
        <Button
          size="small"
          variant="line"
          className="flex items-center justify-center gap-8 px-10 py-8"
          onClick={handleRecalculate}
          disabled={isRecalculating}
        >
          {isRecalculating ? (
            <ArrowClockwise size={18} weight="bold" className="animate-spin" />
          ) : (
            <>
              <ArrowClockwise size={18} weight="bold" />
              {t('button.recalculate')}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
